<template>
  <div v-if="isOpen" class="ownership-notice-overlay" @click="handleClose">
    <div
      :class="['ownership-notice-modal', currentLanguage === 'ar' ? 'rtl' : 'ltr']"
      @click.stop
    >
      <!-- Icon Header -->
      <div class="ownership-icon">
        <span class="notice-icon">{{ text.icon }}</span>
      </div>

      <!-- Title -->
      <h2 class="ownership-title">{{ text.title }}</h2>

      <!-- Subtitle -->
      <div class="ownership-subtitle">{{ text.subtitle }}</div>

      <!-- Description -->
      <p class="ownership-description">{{ text.description }}</p>

      <!-- Checkmark Points -->
      <div class="ownership-points">
        <div v-for="(point, index) in text.points" :key="index" class="ownership-point">
          <span class="checkmark">✓</span>
          <span class="point-text">{{ point }}</span>
        </div>
      </div>

      <!-- Footer -->
      <div v-if="text.footer" class="ownership-footer">{{ text.footer }}</div>

      <!-- Action Button -->
      <button class="ownership-button" @click="handleClose">
        {{ text.button }}
      </button>
    </div>
  </div>
</template>

<script>
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default {
  name: 'OwnershipNoticeModal',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    currentLanguage: {
      type: String,
      required: true
    },
    restaurantId: {
      type: String,
      required: true
    },
    userName: {
      type: String,
      default: 'Unknown'
    }
  },
  data() {
    return {
      openedAt: null,
      content: {
        ar: {
          icon: '🔔',
          title: 'إعلان مهم',
          subtitle: 'انتقلت ملكية Table Tech إلى شركة Edjaas LLC',
          description: 'تم نقل ملكية Table Tech من H1-Software إلى Edjaas LLC',
          points: [
            'الخدمة مستمرة بدون أي انقطاع',
            'نفس الدعم ونفس الجودة',
            'لا يلزمك القيام بأي إجراء'
          ],
          footer: '',
          button: 'فهمت'
        },
        de: {
          icon: '🔔',
          title: 'Wichtige Mitteilung',
          subtitle: 'Table Tech gehört jetzt zu Edjaas LLC',
          description: 'H1-Software hat Table Tech an Edjaas LLC übertragen',
          points: [
            'Service läuft unverändert weiter',
            'Gleicher Support, gleiche Qualität',
            'Keine Aktion erforderlich'
          ],
          footer: '',
          button: 'Verstanden'
        }
      }
    };
  },
  computed: {
    text() {
      return this.content[this.currentLanguage] || this.content.ar;
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.openedAt = new Date();
        console.log('📢 Ownership notice opened at:', this.openedAt);
        console.log('📢 Modal props received:', {
          restaurantId: this.restaurantId,
          userName: this.userName,
          currentLanguage: this.currentLanguage
        });
      }
    }
  },
  methods: {
    formatGermanTime(date) {
      const germanTimeOptions = {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      };

      const formatter = new Intl.DateTimeFormat('de-DE', germanTimeOptions);
      const parts = formatter.formatToParts(date);
      const values = {};
      parts.forEach(({ type, value }) => {
        values[type] = value;
      });
      return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}:${values.second}`;
    },

    async handleClose() {
      console.log('🔘 Close button clicked - starting logging process');

      const closedAt = new Date();
      const readingTime = this.openedAt
        ? Math.round((closedAt - this.openedAt) / 1000) // seconds
        : 0;

      const logData = {
        app: 'OrderDisplay',
        userName: this.userName || 'unknown',
        language: this.currentLanguage,
        openedAt: this.openedAt ? this.formatGermanTime(this.openedAt) : null,
        closedAt: this.formatGermanTime(closedAt),
        readingTimeSeconds: readingTime,
        timestamp: serverTimestamp(),
        acknowledgedAt: serverTimestamp()
      };

      console.log('📝 Preparing to log ownership notice acknowledgment:', logData);

      // Log to Firestore inside restaurant's collection
      try {
        console.log('🔥 Attempting to write to Firestore...');

        const firestore = getFirestore(this.$firebase.app());
        const collectionRef = collection(firestore, `restaurants/${this.restaurantId}/ownershipNoticeLog`);

        console.log('📁 Collection reference created for restaurant:', this.restaurantId);

        const docRef = await addDoc(collectionRef, logData);

        console.log('✅ Ownership notice log saved to Firestore with ID:', docRef.id);
      } catch (error) {
        console.error('❌ Error logging ownership notice:', error);
        console.error('Error name:', error?.name);
        console.error('Error message:', error?.message);
        console.error('Error code:', error?.code);
        console.error('Error stack:', error?.stack);
      }

      // Emit close event
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Ownership Notice Modal Styles */

.ownership-notice-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease-out;
}

.ownership-notice-modal {
  background: linear-gradient(135deg, var(--ods-surface) 0%, var(--ods-surface) 100%);
  border-radius: var(--ods-radius-lg);
  padding: 40px;
  max-width: 550px;
  width: 90%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.8);
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid var(--ods-line);
  font-family: inherit;
}

.ownership-notice-modal.rtl {
  direction: rtl;
  text-align: right;
}

.ownership-notice-modal.ltr {
  direction: ltr;
  text-align: left;
}

/* Icon */
.ownership-icon {
  text-align: center;
  margin-bottom: 20px;
}

.notice-icon {
  font-size: 56px;
  display: inline-block;
  animation: bounce 1s ease-in-out infinite;
}

/* Title */
.ownership-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--ods-ink);
  margin: 0 0 16px 0;
  text-align: center;
  font-family: inherit;
}

/* Subtitle */
.ownership-subtitle {
  font-size: 20px;
  font-weight: 600;
  color: var(--ods-accent-ink);
  margin-bottom: 12px;
  text-align: center;
  line-height: 1.4;
}

/* Description */
.ownership-description {
  font-size: 16px;
  color: var(--ods-ink-2);
  margin: 0 0 28px 0;
  text-align: center;
  line-height: 1.6;
}

/* Points List */
.ownership-points {
  background: var(--ods-surface-2);
  border-radius: var(--ods-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid var(--ods-line);
}

.ownership-point {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  font-size: 15px;
  color: var(--ods-ink);
}

.ownership-point:last-child {
  margin-bottom: 0;
}

.checkmark {
  font-size: 18px;
  font-weight: bold;
  color: #22c55e;
  min-width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(34, 197, 94, 0.15);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.point-text {
  flex: 1;
  line-height: 1.5;
}

/* Footer */
.ownership-footer {
  font-size: 14px;
  color: var(--ods-ink-3);
  font-style: italic;
  text-align: center;
  margin-bottom: 24px;
  padding: 16px;
  background: rgba(59, 130, 246, 0.05);
  border-radius: var(--ods-radius);
  border: 1px solid rgba(59, 130, 246, 0.15);
}

/* Button */
.ownership-button {
  width: 100%;
  padding: 16px 32px;
  background: linear-gradient(135deg, var(--ods-accent) 0%, #8b5cf6 100%);
  color: var(--ods-ink);
  border: none;
  border-radius: var(--ods-radius-lg);
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.3);
}

.ownership-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 35px -5px rgba(59, 130, 246, 0.5);
}

.ownership-button:active {
  transform: translateY(0);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Responsive */
@media (max-width: 640px) {
  .ownership-notice-modal {
    padding: 32px 24px;
    max-width: 95%;
  }

  .ownership-title {
    font-size: 24px;
  }

  .ownership-subtitle {
    font-size: 18px;
  }

  .ownership-description {
    font-size: 14px;
  }

  .ownership-point {
    font-size: 14px;
  }

  .ownership-button {
    font-size: 16px;
    padding: 14px 28px;
  }
}
</style>
